import { FC, useEffect, useState, useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Chip,
  List,

  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,

  Card,
  CardContent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ExaminationSection,
  fetchExaminationSections,
  fetchGeneralAnswers,
  GeneralAnswer,
  fetchPatientHealthData,
} from './examinationService';
import LoadingAnimation from '../../../../components/LoadingAnimation';


interface PhysicalExaminationProps {
  patientId: string;
}

// Type Guard for GeneralAnswer
const isValidGeneralAnswer = (answer: any): answer is GeneralAnswer => {
  return answer && typeof answer.id === 'number' && typeof answer.name === 'string';
};

const PhysicalExamination: FC<PhysicalExaminationProps> = ({ patientId }) => {

  const [openDialog, setOpenDialog] = useState(false);
  const [currentSlug, setCurrentSlug] = useState<string | null>(null);
  const [newNote, setNewNote] = useState<string>('');
  const [examinationSections, setExaminationSections] = useState<ExaminationSection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isNoteValid, setIsNoteValid] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [generalAnswers, setGeneralAnswers] = useState<Record<string, GeneralAnswer[]>>({});
  const [customNotes, setCustomNotes] = useState<Record<string, string>>({});
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string[]>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');
  const [hasChanges, setHasChanges] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const oldValuesRef = useRef<Record<string, { date: string; others: object[];[key: string]: boolean | object[] | string }>>({});

  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {

    const fetchSectionsAndAnswers = async () => {
      setLoading(true);
      try {
        const sections = await fetchExaminationSections(patientId);
        setExaminationSections(sections);

        const expandedPanels = sections.map((_, index) => `${index}`);
        setExpanded(expandedPanels);

        await Promise.all(
          sections.map(async (section) => {
            const slug = section.slug;
            const answers = await fetchGeneralAnswers(patientId, slug);

            // Validate answers
            if (!Array.isArray(answers)) {
              throw new Error(`Expected an array of answers for section ${slug}, but received: ${JSON.stringify(answers)}`);
            }

            const validAnswers = answers.filter(isValidGeneralAnswer);
            if (validAnswers.length !== answers.length) {

            }

            setGeneralAnswers((prev) => ({ ...prev, [slug]: validAnswers || [] }));
            setSelectedAnswers((prev) => ({ ...prev, [slug]: [] }));

            const today = new Date().toISOString().split('T')[0];
            await fetchHealthDataForSection(slug, today, validAnswers);
          })
        );

      } catch (err: any) {
        console.log(err);
        setError(err.message || 'Failed to fetch examination sections');
      } finally {
        setLoading(false);
      }
    };

    fetchSectionsAndAnswers();
  }, [patientId]);


  const fetchHealthDataForSection = async (slug: string, today: string, answers: GeneralAnswer[]) => {


    try {
      const fetchedHealthData = await fetchPatientHealthData(patientId, slug, today, today);
      console.log('fetchedHealthData', patientId, slug, today, today);
      if (fetchedHealthData?.data?.patient_healths) {
        const health = fetchedHealthData.data.patient_healths;

        if (health.length > 0) {
          const latestHealth = health[0];

          oldValuesRef.current[slug] = { ...latestHealth.values };

          const updatedSelected = Object.entries(latestHealth.values).reduce((acc, [key, value]) => {
            if (typeof value === 'boolean' && value) {
              const answerId = answers.find(ans => `${slug}${ans.name.replace(/\s+/g, '-').toLowerCase()}` === key)?.id;
              if (answerId !== undefined) acc.push(String(answerId));
            }
            return acc;
          }, [] as string[]);

          setSelectedAnswers((prev) => ({ ...prev, [slug]: updatedSelected }));

          // Set customNotes directly from the others array
          const notes = Array.isArray(latestHealth.values.others)
            ? latestHealth.values.others.join(', ') || '' // Join the array elements
            : '';
          setCustomNotes((prev) => ({ ...prev, [slug]: notes }));
        } else {
          oldValuesRef.current[slug] = { date: today, others: [] };
          setSelectedAnswers((prev) => ({ ...prev, [slug]: [] }));
          setCustomNotes((prev) => ({ ...prev, [slug]: '' }));
        }
      } else {
        oldValuesRef.current[slug] = { date: today, others: [] };
      }
    } catch (error: any) {
      oldValuesRef.current[slug] = { date: today, others: [] };
      setSelectedAnswers((prev) => ({ ...prev, [slug]: [] }));
      setCustomNotes((prev) => ({ ...prev, [slug]: '' }));
    } finally {

    }
  };


  const handleChange = (panel: string) => () => {
    setExpanded((prev) => (prev.includes(panel) ? prev.filter((id) => id !== panel) : [...prev, panel]));
  };
  if (loading) {
    return <LoadingAnimation />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }


  const getSaveButtonText = () => {

    return newNote.trim() ? (customNotes[currentSlug!] ? 'Update' : 'Add') : 'Save';
  };


  return (
    <div>
      {examinationSections.map((section, index) => (
        <Accordion
          key={section.slug}
          expanded={expanded.includes(String(index))}
          onChange={handleChange(String(index))}
          sx={{
            backgroundColor: '#ffffff',

          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#0870b4' }} />}>
            <Typography
              className="font-jost"
              sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 'bold', color: hasChanges[section.slug] ? '#0870b4' : '#0870b4', }}
            >
              {section.name}
            </Typography>


            {saving[section.slug] && <LoadingAnimation />}
          </AccordionSummary>
          <AccordionDetails
            style={{
              padding: '6px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px', // Increased border radius for a softer look
              boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.7)', // Deeper shadow for more depth
            }}
          >
            <List>
              {generalAnswers[section.slug]?.length ? (
                generalAnswers[section.slug].map((answer, answerIndex) => {
                  if (!isValidGeneralAnswer(answer)) {
                    console.warn(`Invalid answer detected at index ${answerIndex} for section "${section.slug}":`, answer);
                    return null; // Skip rendering this Chip
                  }

                  return (
                    <Card
                      key={answer.id}
                      style={{
                        backgroundColor: selectedAnswers[section.slug]?.includes(String(answer.id)) ? '#007bff' : '#e0e0e0', // Light gray background for unselected cards
                        color: selectedAnswers[section.slug]?.includes(String(answer.id)) ? '#fff' : '#000',
                        border: `1px solid ${selectedAnswers[section.slug]?.includes(String(answer.id)) ? '#007bff' : '#ccc'}`, // Light border for unselected cards
                        margin: '4px',
                        fontFamily: 'Jost, sans-serif',
                        borderRadius: '20px', // More rounded corners for cards
                        fontSize: '16px', // Increase font size
                        padding: '5px 15px', // Adjust padding to match chip appearance
                        display: 'inline-block', // Ensure cards are displayed inline like chips
                        transition: 'all 0.3s ease', // Smooth transition for hover effect
                     
                      }}
                      
                    >
                      <CardContent style={{ padding: 0, textAlign: 'center' }}> {/* Ensure text is centered */}
                        {answer.name}
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Typography>No answers available</Typography>
              )}
            </List>
            {oldValuesRef.current[section.slug]?.others?.length > 0 && (
              <ListItem>
                <h1 className='pr-2 font-bold'>Notes:</h1>

                {oldValuesRef.current[section.slug].others.map((item, idx) => {
                  if (typeof item !== 'string') return null; // Skip invalid items
                  return (
                    <>
                      <Card
                        key={idx}
                        style={{
                          display: 'inline-block',
                          color: 'black',
                          borderRadius: '12px', // Rounded corners
                          padding: '6px 12px', // Padding for better spacing
                          fontSize: '14px',
                          position: 'relative',
                          maxWidth: '100%', // Full width of the parent container

                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // Subtle shadow
                          overflow: 'visible', // Make sure the icon outside the card isn't clipped
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 'normal',
                            fontSize: '1.1em', // Slightly larger font for better readability
                            wordWrap: 'break-word', // Break long words if needed
                            whiteSpace: 'normal',
                          }}
                        >
                          {item}
                        </span>


                      </Card>

                    </>

                  );
                })
                }
              </ListItem>
            )}
            <div style={{ height: '1px', backgroundColor: '#e0e0e0', margin: '8px 0', borderRadius: '2px' }} />


          </AccordionDetails>
        </Accordion>
      ))}


    </div >
  );
};

export default PhysicalExamination;
